import React, { useMemo } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { CloseIcon, PlusIcon, DeleteIcon, InvoiceIcon, SpinnerIcon } from "../../../Icons";
import EvaluationInput from "../../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../../EvaluationNew/components/InputDatePicker";
import { displayDateUS } from "../../../utils/dateUtils";
import EvaluationSelect from "../../EvaluationNew/components/EvaluationSelect";
import Table from "../../../components/UI/Table";
import { FORM_FIELD } from "../../../ui/formTheme";
import {
  INVOICE_THEME,
  INVOICE_BTN,
  INVOICE_MODAL,
  INVOICE_SECTION_TITLE,
} from "../invoiceTheme";
import {
  INVOICE_VISIT_TYPE_OPTIONS,
  computeLineItemTotal,
  formatHoursDisplay,
  isFixedVisitType,
  isHourlyVisitType,
  isLegacyClinicalVisitType,
  roundMoney,
  visitTypeDisplayLabel,
} from "../invoiceLineItem";

const FIELD_GRID = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-3 items-start";

/** Compact single-row line item — fixed label / control / error slots keep inputs aligned. */
const LINE_ITEM_LABEL =
  "block text-[11px] font-medium text-slate-700 mb-1 min-h-[15px] leading-tight";
const LINE_ITEM_ERROR = "text-red-500 text-[10px] mt-0.5 leading-tight";
/** Overrides default control padding/height — keep w-full + min-w-0 from FORM_FIELD.control */
const LINE_ITEM_CONTROL =
  "!h-9 !py-0 !px-2.5 text-sm !rounded-md leading-none box-border min-w-0 max-w-full";

/** Each cell is flex-none with fixed width — wrap on narrow viewports so dropdowns are not clipped. */
const LINE_CELL = "flex-none min-w-0 max-w-full";

const LINE_ITEM_ROW =
  "flex flex-wrap items-start gap-3 w-full";

const LINE_COL = {
  patient: `${LINE_CELL} w-[200px]`,
  date: `${LINE_CELL} w-[148px]`,
  visitType: `${LINE_CELL} w-[176px]`,
  amount: `${LINE_CELL} w-[104px]`,
  amountWide: `${LINE_CELL} w-[116px]`,
  total: `${LINE_CELL} w-[104px]`,
  action: `${LINE_CELL} w-[112px]`,
};

const LINE_FIELD_PROPS = {
  labelClassName: LINE_ITEM_LABEL,
  inputClassName: LINE_ITEM_CONTROL,
  errorClassName: LINE_ITEM_ERROR,
  reserveErrorSpace: true,
};

const rateValidation = (label = "Rate") => ({
  required: `${label} is required`,
  validate: (v) => {
    if (v === "" || v === null || v === undefined) {
      return `${label} is required`;
    }
    const n = Number(v);
    if (Number.isNaN(n)) return `${label} must be a number`;
    if (n < 0) return `${label} must be 0 or greater`;
    return true;
  },
});

const blockNegativeNumberKeys = (e) => {
  if (e.key === "-" || e.key === "e" || e.key === "E") {
    e.preventDefault();
  }
};

const TotalPreview = ({ value, className = "", labelClassName = LINE_ITEM_LABEL }) => (
  <div className={`${className} min-w-0 max-w-full overflow-hidden`}>
    <label className={labelClassName}>Total ($)</label>
    <div
      className={`${FORM_FIELD.readonlyBox} ${LINE_ITEM_CONTROL} !px-2.5 flex items-center font-semibold text-indigo-900 tabular-nums box-border w-full min-w-0`}
      aria-live="polite"
    >
      {value}
    </div>
    <p className={`${LINE_ITEM_ERROR} min-h-[14px] invisible`} aria-hidden="true">
      {"\u00a0"}
    </p>
  </div>
);

const LineItemEntryFields = ({ visitType }) => {
  const hourly = isHourlyVisitType(visitType);
  const fixed = isFixedVisitType(visitType);
  const clinical = isLegacyClinicalVisitType(visitType);

  const watchHours = useWatch({ name: "newItemHours" });
  const watchRate = useWatch({ name: "newItemRate" });
  const previewTotal = useMemo(() => {
    if (!hourly) return null;
    return roundMoney((Number(watchHours) || 0) * (Number(watchRate) || 0));
  }, [hourly, watchHours, watchRate]);

  if (fixed) {
    return (
      <EvaluationInput
        className={LINE_COL.amountWide}
        label="Total ($)"
        name="newItemRate"
        type="number"
        step="0.01"
        min="0"
        placeholder="0.00"
        isRequired
        {...LINE_FIELD_PROPS}
        rules={rateValidation("Total")}
        onKeyDown={blockNegativeNumberKeys}
      />
    );
  }

  if (hourly) {
    return (
      <>
        <EvaluationInput
          className={LINE_COL.amount}
          label="Total Hours"
          name="newItemHours"
          type="number"
          step="0.25"
          min="0"
          placeholder="0.00"
          isRequired
          {...LINE_FIELD_PROPS}
          rules={rateValidation("Total Hours")}
          onKeyDown={blockNegativeNumberKeys}
        />
        <EvaluationInput
          className={LINE_COL.amount}
          label="Rate ($)"
          name="newItemRate"
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          isRequired
          {...LINE_FIELD_PROPS}
          rules={rateValidation("Rate")}
          onKeyDown={blockNegativeNumberKeys}
        />
        <TotalPreview
          className={LINE_COL.total}
          value={previewTotal != null ? previewTotal.toFixed(2) : "0.00"}
        />
      </>
    );
  }

  if (clinical) {
    return (
      <EvaluationInput
        className={LINE_COL.amountWide}
        label="Rate ($)"
        name="newItemRate"
        type="number"
        step="0.01"
        min="0"
        placeholder="0.00"
        isRequired
        {...LINE_FIELD_PROPS}
        rules={rateValidation("Rate")}
        onKeyDown={blockNegativeNumberKeys}
      />
    );
  }

  return null;
};

const CreateInvoiceModal = ({
  show,
  onClose,
  methods,
  onSubmit,
  isLoading,
  currentInvoiceItems,
  handleAddItem,
  handleRemoveItem,
  visitTypes,
  formatCurrency,
  totalCurrentAmount,
  isEdit = false,
}) => {
  const watchVisitType = useWatch({
    control: methods.control,
    name: "newItemVisitType",
  });

  const columns = [
    {
      header: "Patient Name",
      accessor: "patientName",
      className: "min-w-0 pl-3 pr-2 align-middle",
    },
    {
      header: "Visit Date",
      accessor: "visitDate",
      className: "whitespace-nowrap px-2 align-middle",
      render: (row) => displayDateUS(row.visitDate) || "—",
    },
    {
      header: "Visit Type",
      accessor: "visitType",
      className: "whitespace-nowrap px-2 align-middle",
      render: (row) => visitTypeDisplayLabel(row.visitType),
    },
    {
      header: "Hours",
      accessor: "hours",
      className: "whitespace-nowrap px-2 text-right align-middle tabular-nums",
      render: (row) =>
        isHourlyVisitType(row.visitType)
          ? formatHoursDisplay(row.hours)
          : "—",
    },
    {
      header: "Rate ($)",
      accessor: "rate",
      className: "whitespace-nowrap px-2 text-right align-middle tabular-nums",
      render: (row) =>
        isHourlyVisitType(row.visitType) || isLegacyClinicalVisitType(row.visitType)
          ? formatCurrency(row.rate)
          : "—",
    },
    {
      header: "Total ($)",
      id: "total",
      className: "whitespace-nowrap px-2 text-right align-middle tabular-nums font-semibold",
      render: (row) => formatCurrency(computeLineItemTotal(row)),
    },
    {
      header: "Actions",
      id: "actions",
      className: "whitespace-nowrap pl-2 pr-3 text-right align-middle",
      render: (row) => (
        <button
          type="button"
          onClick={() => handleRemoveItem(row.id)}
          className={INVOICE_BTN.danger}
          title="Remove item"
        >
          <DeleteIcon size={13} />
          Remove
        </button>
      ),
    },
  ];

  if (!show) return null;

  const visitTypeOptions =
    visitTypes?.length > 0
      ? visitTypes.map((t) =>
          typeof t === "string" ? { value: t, label: visitTypeDisplayLabel(t) } : t,
        )
      : INVOICE_VISIT_TYPE_OPTIONS;

  return (
    <div
      className={INVOICE_MODAL.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-invoice-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose?.();
      }}
    >
      <div className={INVOICE_MODAL.panel}>
        <div className={INVOICE_MODAL.head}>
          <div className="min-w-0">
            <h2 id="create-invoice-title" className={INVOICE_MODAL.title}>
              {isEdit ? "Edit Invoice" : "Create New Invoice"}
            </h2>
            <p className={INVOICE_MODAL.subtitle}>
              {isEdit
                ? "Update invoice details and visit line items"
                : "Add invoice details and visit line items"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className={INVOICE_MODAL.close}
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <FormProvider {...methods}>
          <div className={INVOICE_MODAL.body}>
            <div
              className={`rounded-xl border p-4 bg-indigo-50/30 ${INVOICE_THEME.section.wrap}`}
            >
              <h3 className={INVOICE_SECTION_TITLE}>1. Invoice Details</h3>
              <div className={FIELD_GRID}>
                <EvaluationInput
                  label="Invoice Number"
                  name="invoiceNumber"
                  placeholder="Enter invoice number"
                  isRequired
                  rules={{ required: "Invoice Number is required" }}
                />
                <EvaluationInput
                  label="Company Name"
                  name="companyName"
                  placeholder="Enter company name"
                  isRequired
                  rules={{ required: "Company Name is required" }}
                  disabled
                />
                <InputDatePicker
                  label="From Date"
                  name="fromDate"
                  formatSeparator="/"
                  isRequired
                  rules={{ required: "From Date is required" }}
                />
                <InputDatePicker
                  label="To Date"
                  name="toDate"
                  formatSeparator="/"
                  isRequired
                  rules={{ required: "To Date is required" }}
                />
                <InputDatePicker
                  label="Due Date"
                  name="dueDate"
                  formatSeparator="/"
                  allowFuture
                />
                <div className="sm:col-span-2 lg:col-span-3">
                  <EvaluationInput
                    label="Notes"
                    name="notes"
                    type="text"
                    placeholder="Optional notes"
                  />
                </div>
              </div>
            </div>

            <div
              className={`rounded-xl border p-4 bg-indigo-50/30 ${INVOICE_THEME.section.wrap}`}
            >
              <h3 className={INVOICE_SECTION_TITLE}>2. Add Line Item</h3>
              <div className={LINE_ITEM_ROW}>
                  <EvaluationInput
                    className={LINE_COL.patient}
                    label="Patient Name"
                    name="newItemPatientName"
                    placeholder="Patient name"
                    isRequired
                    {...LINE_FIELD_PROPS}
                    rules={{
                      required: "Patient Name is required",
                      validate: (v) =>
                        (typeof v === "string" && v.trim().length > 0) ||
                        "Patient Name is required",
                    }}
                  />
                  <InputDatePicker
                    className={LINE_COL.date}
                    label="Visit Date"
                    name="newItemVisitDate"
                    formatSeparator="/"
                    compact
                    isRequired
                    {...LINE_FIELD_PROPS}
                    rules={{ required: "Visit Date is required" }}
                  />
                  <EvaluationSelect
                    className={LINE_COL.visitType}
                    label="Visit Type"
                    name="newItemVisitType"
                    placeholder="Select type"
                    isRequired
                    {...LINE_FIELD_PROPS}
                    options={visitTypeOptions}
                    rules={{
                      required: "Visit Type is required",
                      validate: (v) =>
                        (typeof v === "string" && v.trim().length > 0) ||
                        "Visit Type is required",
                    }}
                  />
                  <LineItemEntryFields visitType={watchVisitType} />
                  <div className={`${LINE_COL.action} min-w-0`}>
                    <span
                      className={`${LINE_ITEM_LABEL} invisible select-none`}
                      aria-hidden="true"
                    >
                      Add
                    </span>
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className={`${INVOICE_BTN.outline} h-9 w-full min-w-0 whitespace-nowrap box-border`}
                    >
                      <PlusIcon size={14} />
                      Add Item
                    </button>
                    <p
                      className={`${LINE_ITEM_ERROR} min-h-[14px] invisible`}
                      aria-hidden="true"
                    >
                      {"\u00a0"}
                    </p>
                  </div>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                Fill patient, visit date, and visit type, then click Add Item.
              </p>
            </div>

            <div
              className={`rounded-xl border overflow-x-auto bg-white ${INVOICE_THEME.section.wrap}`}
            >
              <Table
                columns={columns}
                data={currentInvoiceItems}
                keyField="id"
                loading={false}
                showSrNo
                density="compact"
                embedded
                headTone={INVOICE_THEME.tableHead}
                noDataMessage="No records found."
              />
              <div className="px-4 py-3 flex justify-between items-center border-t border-indigo-100 bg-indigo-50/40">
                <span className="text-sm font-medium text-slate-700">
                  Total Items: {currentInvoiceItems.length}
                </span>
                <div className="text-sm font-bold text-slate-900 tabular-nums">
                  <span className="text-slate-500 font-medium mr-2">
                    Total Amount:
                  </span>
                  {formatCurrency(totalCurrentAmount)}
                </div>
              </div>
            </div>
          </div>

          <div className={INVOICE_MODAL.footer}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={INVOICE_BTN.modalCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={isLoading}
              className={INVOICE_BTN.modalPrimary}
            >
              {isLoading ? (
                <>
                  <SpinnerIcon className="animate-spin" size={14} />
                  Processing...
                </>
              ) : (
                <>
                  <InvoiceIcon size={14} />
                  {isEdit ? "Update Invoice" : "Create Invoice"}
                </>
              )}
            </button>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
